import { useState } from 'react';
import { ChevronDown, ChevronRight, Hash, MessageSquare, User } from 'lucide-react';
import { cn, formatEtTime } from '@eo/shared';

import { groupTranscript, transcriptAuthorLabel, transcriptConversationLabel } from './helpers';
import type { SlackTranscriptProps } from './types';

// The title on a Slack block is a model's phrase for a conversation, and it can
// be wrong about what the conversation was. Collapsed by default because the
// CFO who agrees with the title should not have to scroll past the evidence to
// reach Save; one click for the one who does not.
//
// Laid out as the conversation it was: the CFO's own messages on the right,
// everyone else's on the left. Reading a transcript is remembering a day, and a
// list of labelled lines is not how anyone remembers one.
export function SlackTranscript({ lines }: SlackTranscriptProps) {
  const [open, setOpen] = useState(false);
  const groups = groupTranscript(lines);

  return (
    <section className="overflow-hidden rounded-8 border border-border-faint">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 bg-muted px-3 py-2.5 text-left transition-colors hover:bg-muted/60"
      >
        {open ? (
          <ChevronDown className="size-4 shrink-0 text-text-tertiary" />
        ) : (
          <ChevronRight className="size-4 shrink-0 text-text-tertiary" />
        )}
        <span className="text-sm font-medium text-text-primary">Why this was suggested</span>
        <span className="ml-auto flex items-center gap-1.5 text-xs text-text-tertiary">
          <MessageSquare className="size-3.5" />
          {lines.length}
        </span>
      </button>

      {open && (
        <div className="max-h-80 overflow-y-auto border-t border-border-faint">
          {groups.map((group) => (
            <div key={group.conversationId}>
              {/* Sticky because a long transcript scrolls past its own heading,
                  and a message with no channel above it is unattributable. */}
              <h4 className="sticky top-0 z-10 flex items-center gap-1 border-b border-border-faint bg-muted px-3 py-1.5 text-xs font-medium text-text-secondary">
                {group.conversationKind === 'dm' ? (
                  <User className="size-3 text-text-tertiary" />
                ) : (
                  <Hash className="size-3 text-text-tertiary" />
                )}
                {transcriptConversationLabel(group)}
              </h4>

              <div className="flex flex-col gap-3 px-3 py-3">
                {group.runs.map((run) => {
                  const mine = run.speaker === 'you';
                  return (
                    <article
                      key={run.key}
                      className={cn('flex flex-col gap-1', mine ? 'items-end' : 'items-start')}
                    >
                      {/* Only the other side is named. Labelling every one of
                          the CFO's own turns "You" is noise on the side of the
                          conversation the alignment already identifies. */}
                      <div className="flex items-baseline gap-2 px-1">
                        {!mine && (
                          <span className="text-xs font-semibold text-text-primary">
                            {transcriptAuthorLabel(run, group)}
                          </span>
                        )}
                        <time className="text-[11px] tabular-nums text-text-tertiary">
                          {formatEtTime(run.ts)}
                        </time>
                      </div>

                      {/* A run is one turn, so its messages stack as separate
                          bubbles under one name. Slack's own line breaks are
                          part of what was said and are kept. */}
                      <div
                        className={cn(
                          'flex max-w-[85%] flex-col gap-1',
                          mine ? 'items-end' : 'items-start',
                        )}
                      >
                        {run.texts.map((text, index) => (
                          <p
                            key={`${run.key}:${index}`}
                            className={cn(
                              'whitespace-pre-wrap break-words rounded-12 px-3 py-2 text-xs leading-relaxed',
                              mine
                                ? 'bg-primary/10 text-text-primary'
                                : 'border border-border-faint bg-muted text-text-secondary',
                              // The squared corner points at the speaker, and
                              // only on the turn's last bubble, so a stack
                              // reads as one person still talking.
                              index === run.texts.length - 1 &&
                                (mine ? 'rounded-br-sm' : 'rounded-bl-sm'),
                            )}
                          >
                            {text}
                          </p>
                        ))}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
